import { useState, useEffect, useCallback } from 'react';
import { useTeamsUserCredential } from '@microsoft/teamsfx-react';
import config from '../../config';

/**
 * Enhanced authentication hook with comprehensive error handling and consent management
 */
export const useEnhancedAuth = () => {
  // Check for mobile auth data on initialization
  const isMobileDevice = /TeamsMobile|Android.*Teams|iPhone.*Teams/i.test(navigator.userAgent);
  
  // Clean up any stale auth data older than 24 hours
  if (isMobileDevice) {
    const authTimestamp = localStorage.getItem('mobile_auth_timestamp');
    if (authTimestamp) {
      const authAge = Date.now() - parseInt(authTimestamp);
      if (authAge > 24 * 60 * 60 * 1000) { // 24 hours
        console.log('🧹 Clearing stale mobile auth data');
        localStorage.removeItem('mobile_auth_user');
        localStorage.removeItem('mobile_auth_complete');
        localStorage.removeItem('mobile_auth_timestamp');
        localStorage.removeItem('teams_sso_token');
      }
    }
  }
  
  // Check both localStorage (new) and sessionStorage (fallback) for mobile auth
  const storedMobileUser = isMobileDevice ? 
    (localStorage.getItem('mobile_auth_user') || sessionStorage.getItem('mobile_auth_user')) : null;
  const mobileAuthComplete = isMobileDevice ? 
    (localStorage.getItem('mobile_auth_complete') === 'true' || sessionStorage.getItem('mobile_auth_complete') === 'true') : false;
  
  // Debug mobile auth state
  if (isMobileDevice) {
    console.log('📱 Mobile Auth State Check:', {
      hasMobileUser: !!storedMobileUser,
      mobileAuthComplete,
      userData: storedMobileUser ? JSON.parse(storedMobileUser) : null,
      fromLocalStorage: !!localStorage.getItem('mobile_auth_user'),
      fromSessionStorage: !!sessionStorage.getItem('mobile_auth_user')
    });
  }
  
  const [authState, setAuthState] = useState({
    isAuthenticated: !!(isMobileDevice && storedMobileUser && mobileAuthComplete),
    isLoading: !(isMobileDevice && storedMobileUser && mobileAuthComplete),  // Don't load if already authenticated
    error: null,
    user: storedMobileUser ? JSON.parse(storedMobileUser) : null,
    needsConsent: false,
    consentType: null,
    tenantId: null,
  });
  
  // Add a loading timeout to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only timeout if STILL loading and NOT authenticated
      if (authState.isLoading && !authState.isAuthenticated) {
        console.log('⏱️ Auth loading timeout - showing sign-in');
        setAuthState(prev => ({
          ...prev,
          isLoading: false
        }));
      }
    }, 8000); // 8 second max loading time - give Teams time to show "Almost there..."
    
    return () => clearTimeout(timer);
  }, [authState.isLoading, authState.isAuthenticated]); // Re-run when auth state changes

  // 🔧 FIX: Initialize without scopes to prevent circular dependency
  // Only request Graph scope initially - API scope will be requested on-demand
  const { loading, theme, themeString, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint,
    clientId: config.clientId,
    scopes: ['https://graph.microsoft.com/User.Read'] // Only basic Graph scope
  });

  // Error analysis function
  const analyzeAuthError = useCallback((error) => {
    if (!error) return null;

    const errorCode = error.errorCode || error.error || '';
    const errorMessage = error.errorMessage || error.error_description || error.message || '';
    
    // Extract tenant ID from error message or context
    const tenantIdMatch = errorMessage.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    const detectedTenantId = tenantIdMatch ? tenantIdMatch[0] : null;

    // Determine consent requirements
    // UiRequiredError means user needs to sign in, NOT that consent is needed
    const isUiRequiredError = errorCode === 'UiRequiredError';
    
    const needsTeamsConsent = errorMessage.includes(config.teamsWebClientId) || 
                             errorMessage.includes('Teams Web Client');
    
    const needsAppConsent = 
        // Don't treat UiRequiredError as consent issue - it's just a login requirement
        (errorMessage.includes(config.clientId) ||
        ['consent_required','admin_consent_required',
         'interaction_required'].includes(errorCode)) && !isUiRequiredError;

    let consentType = null;
    if (needsTeamsConsent && needsAppConsent) consentType = 'both';
    else if (needsTeamsConsent) consentType = 'teams';
    else if (needsAppConsent) consentType = 'app';

    return {
      originalError: error,
      errorCode,
      errorMessage,
      tenantId: detectedTenantId,
      needsConsent: !!(needsTeamsConsent || needsAppConsent),
      consentType,
      needsTeamsConsent,
      needsAppConsent,
      isUiRequiredError,  // Add this flag so we know it's a login issue
    };
  }, [config.clientId]);

  // Handle authentication state changes
  useEffect(() => {
    const updateAuthState = async () => {
      // Skip TeamsFx auth flow on mobile if we already have stored auth
      if (isMobileDevice && authState.isAuthenticated && authState.user) {
        console.log('📱 Mobile: Using stored authentication');
        return;
      }
      
      if (loading) {
        setAuthState(prev => ({ ...prev, isLoading: true }));
        return;
      }

      try {
        if (teamsUserCredential) {
          // Try to get user info to verify authentication
          const userInfo = await teamsUserCredential.getUserInfo();
          
          if (userInfo && userInfo.objectId) {
            // Enhanced user info validation
            const enhancedUserInfo = {
              ...userInfo,
              userId: userInfo.objectId || userInfo.id || userInfo.oid,
              displayName: userInfo.displayName || userInfo.name || 'Unknown User',
              email: userInfo.mail || userInfo.userPrincipalName || userInfo.email,
              tenantId: userInfo.tenantId || userInfo.tid
            };
            
            // 🎯 KEY: Try to get a token to verify we have consent
            // If this succeeds, we HAVE consent. If it fails, we'll catch and analyze the error
            try {
              console.log('🔍 Checking if we have consent by trying to get token...');
              await teamsUserCredential.getToken(['User.Read']);
              console.log('✅ Token acquired successfully - we HAVE consent!');
              
              setAuthState({
                isAuthenticated: true,
                isLoading: false,
                error: null,
                user: enhancedUserInfo,
                needsConsent: false,  // We definitely don't need consent if token succeeded
                consentType: null,
                tenantId: enhancedUserInfo.tenantId || null,
              });
              return;
            } catch (tokenError) {
              console.log('⚠️ Token acquisition failed, analyzing error...', tokenError);
              // Token failed - analyze why
              const errorAnalysis = analyzeAuthError(tokenError);
              
              // If it's just UiRequiredError, we're authenticated but need to sign in
              if (errorAnalysis?.isUiRequiredError) {
                console.log('📝 UiRequiredError - need user to sign in, but not a consent issue');
                setAuthState({
                  isAuthenticated: false,  // Can't proceed without sign-in
                  isLoading: false,
                  error: null,  // Don't show error, just sign-in button
                  user: enhancedUserInfo,
                  needsConsent: false,  // Not a consent issue, just needs sign-in
                  consentType: null,
                  tenantId: enhancedUserInfo.tenantId || null,
                });
              } else if (errorAnalysis?.needsConsent) {
                console.log('🔴 ACTUAL consent required:', errorAnalysis);
                setAuthState({
                  isAuthenticated: false,
                  isLoading: false,
                  error: errorAnalysis,
                  user: enhancedUserInfo,
                  needsConsent: true,  // Real consent issue
                  consentType: errorAnalysis.consentType,
                  tenantId: enhancedUserInfo.tenantId || null,
                });
              } else {
                // Some other error - let it through
                throw tokenError;
              }
              return;
            }
          } else {
            console.warn('User info missing required fields:', userInfo);
            throw new Error('User info validation failed');
          }
        }

        // 🔴 No teamsUserCredential - this is normal during initialization
        console.log('🔴 No Teams credential yet - not authenticated');
        setAuthState({
          isAuthenticated: false,
          isLoading: false,  // IMPORTANT: Set loading to false
          error: null,
          user: null,
          needsConsent: false,
          consentType: null,
          tenantId: null
        });

      } catch (error) {
        console.error('Authentication error:', error);
        
        const errorAnalysis = analyzeAuthError(error);
        
        // Only set needsConsent if the error analysis actually found a consent issue
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: errorAnalysis,
          user: null,
          needsConsent: errorAnalysis?.needsConsent === true, // ONLY true if error says so
          consentType: errorAnalysis?.consentType || null,
          tenantId: errorAnalysis?.tenantId || null,
        });
      }
    };

    updateAuthState();
  }, [loading, teamsUserCredential, analyzeAuthError]);

  // Retry authentication
  const retryAuth = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      needsConsent: false,
    }));
    
    // Force re-evaluation by clearing any cached credentials
    // The useTeamsUserCredential hook should handle the retry
    window.location.reload();
  }, []);

  // Handle consent completion
  const handleConsentCompleted = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      needsConsent: false,
      consentType: null,
    }));
  }, []);

  // Get user token
  const getAccessToken = useCallback(async (scopes = ['https://graph.microsoft.com/.default']) => {
    if (!teamsUserCredential || !authState.isAuthenticated) {
      throw new Error('User not authenticated');
    }

    try {
      const token = await teamsUserCredential.getToken(scopes);
      return token;
    } catch (error) {
      console.error('Token acquisition error:', error);
      const errorAnalysis = analyzeAuthError(error);
      
      setAuthState(prev => ({
        ...prev,
        error: errorAnalysis,
        needsConsent: errorAnalysis?.needsConsent || false,
        consentType: errorAnalysis?.consentType || null,
      }));
      
      throw error;
    }
  }, [teamsUserCredential, authState.isAuthenticated, analyzeAuthError]);

  return {
    // Authentication state
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    user: authState.user,
    
    // Error and consent state
    error: authState.error,
    needsConsent: authState.needsConsent,
    consentType: authState.consentType,
    tenantId: authState.tenantId,
    
    // Actions
    retryAuth,
    handleConsentCompleted,
    getAccessToken,
    
    // TeamsFx compatibility
    theme,
    themeString,
    teamsUserCredential,
  };
};

export default useEnhancedAuth;
